import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {newStepValue} from '../../../../../../../modules/flow/services/flowBuilder/utils/constants';
import {getEditTypes} from '../../../delegatorUtils';
import {EditPermissions} from './../../../../../../../shared/models/node-builder';
import {maxLength, msisdnValidator, required} from '../../../../../../../shared/utils';
import {WaTemplate, WaTemplateIndexUrl} from '../../../../../template/wa-template-preview/wa-template.service';
import isEmpty from 'lodash/isEmpty';

export interface LocationTemplate {
    locationName: string
    address: string
    latitude: string
    longitude: string
}

export interface TemplateForm {
    tag?: string
    bodyVars?: string[]
    websiteUrls?: WaTemplateIndexUrl[]
    couponCode?: string
    otp?: string
    suggestedResponse?: any[]
    mediaVar?: string
    headerVar?: string
    location?: LocationTemplate
}

export interface ActionParamsForm {
    description: string
    sendTo: string;
    clientReferenceId: string
    name: string;

    integrationId: string;
    businessAccountId: string;
    templateId: string;

    success: string;
    error: string;
}

export interface sendTemplateForm {
    actionParamsForm: FormGroup;
    templateContentForm: TemplateForm;
}

export const additionalDataErrorMessage = 'Additional Parameter length exceeded';

export class SendTemplateNode {

    private action: any = {};

    constructor() {
        this.action = {
            actionType: 'sendTemplate'
        };
    }

    static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): sendTemplateForm {

        const actionParamsForm = this.actionParamsTemplate(editPermissions);
        actionParamsForm.patchValue(this.getActionParamsData(stepData));
        const templateContentForm = this.getTemplateData(stepData);

        return { actionParamsForm, templateContentForm };

    }

    static getActionParamsData(stepData: { [index: string]: any }): ActionParamsForm {

        const action = stepData?.action ? stepData?.action : {};
        const actionParams = action?.actionParams ? action?.actionParams : {};
        const result = action?.result ? action?.result : {};

        return {
            description: action?.description ? action?.description : "",
            sendTo: actionParams?.sendTo ? actionParams?.sendTo : "",
            clientReferenceId: actionParams?.clientReferenceId ? actionParams?.clientReferenceId : "",
            name: action?.name ? action?.name : "",
            integrationId: actionParams?.integrationId ? actionParams?.integrationId : "",
            businessAccountId: actionParams?.businessAccountId ? actionParams?.businessAccountId : "",
            templateId: actionParams?.templateId ? actionParams?.templateId : "",
            error: result?.error ? result?.error : newStepValue,
            success: result?.success ? result?.success : newStepValue,
        };
    }

    static getTemplateData(stepData: { [index: string]: any }): TemplateForm {
        const action = stepData?.action ? stepData?.action : {}
        const actionParams = action?.actionParams ? action?.actionParams : {}
        const couponText = action?.actionParams?.parameters?.buttons?.copyCode?.text
        const optText = action?.actionParams?.parameters?.buttons?.otp
        const optDefaultText = action?.actionParams?.parameters?.body?.example && action?.tag === 'Authentication'

        return {
            bodyVars: this.bodyToArray(actionParams?.parameters?.body),
            headerVar: actionParams?.parameters?.header?.text ? actionParams?.parameters?.header?.text[1] : undefined,
            mediaVar: action?.actionParams?.parameters?.header?.media?.fileId ? action?.actionParams?.parameters?.header?.media?.fileId : undefined,
            suggestedResponse: action?.actionParams?.parameters?.buttons?.suggestedResponse,
            websiteUrls: this.urlToArray(actionParams?.parameters?.buttons?.websiteUrl ? actionParams?.parameters?.buttons?.websiteUrl : actionParams?.parameters?.buttons?.websiteUrls),
            couponCode: (couponText || action?.actionParams?.parameters?.buttons?.couponCode) ? couponText || action?.actionParams?.parameters?.buttons?.couponCode : undefined,
            otp: (optDefaultText || optText) ? (optDefaultText || optText) : undefined,
            location: this.toLocation(action?.actionParams?.parameters?.header?.location)
        };
    }

    static toLocation(location): LocationTemplate {
        if (location !== undefined) {
            return {
                locationName: location.name,
                address: location.address,
                latitude: location.latitude,
                longitude: location.longitude
            };
        }

        return undefined;
    }

    static bodyToArray(body: {}): string[] {
        if (body) {
            const bodyArray = []
            for (const bodyKey of Object.keys(body).sort()) {
                bodyArray.push({
                    bodyVariable: body[bodyKey]
                })
            }
            return bodyArray
        }
        return undefined
    }

    static urlToArray(body: {}): WaTemplateIndexUrl[] {
        if (body) {
            const bodyArray = []
            for (const bodyKey of Object.keys(body).sort()) {
                bodyArray.push({
                    index: bodyKey,
                    url: body[bodyKey],
                    text: ""
                })
            }
            return bodyArray
        }
        return undefined
    }

    static actionParamsTemplate(editPermissions: EditPermissions): FormGroup {
        const {editMenuTextOnly, editFlows} = getEditTypes(editPermissions);
        return new FormGroup({
            sendTo: new FormControl({
                value: '',
                disabled: !editMenuTextOnly
            }, [required('MSISDN Number'), msisdnValidator('MSISDN')]),
            clientReferenceId: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            description: new FormControl({
                value: '',
                disabled: !editFlows
            }, required('Description')),
            name: new FormControl({
                value: '',
                disabled: !editFlows
            }, required('Name')),
            integrationId: new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('Integration')]),
            businessAccountId: new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('Account')]),
            templateId: new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('Template')]),
            success: new FormControl(newStepValue),
            error: new FormControl(newStepValue)
        });
    }

    static bodyVarTemplate(editPermissions: EditPermissions): FormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        return new FormGroup({
            bodyVariable: new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('Body variable'), maxLength(60)]),
        });
    }

    static websiteUrlsTemplate(editPermissions: EditPermissions,websiteUrl?: {}): FormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        return new FormGroup({
            url: new FormControl({
                value: websiteUrl['url'] && typeof websiteUrl['index'] === 'string' ? websiteUrl['url']: "",
                disabled: !editFlows
            }, [required('Dynamic Link')]),
            text: new FormControl({
                value:  websiteUrl['text'],
                disabled: true
            }),
            index: new FormControl({
                value:  websiteUrl['index'],
                disabled: true
            }),
        });
    }

    static buildTemplate(editPermissions: EditPermissions, template: TemplateForm): FormGroup {
        const {editFlows} = getEditTypes(editPermissions);

        const formGroup = new FormGroup({});
        if (template.couponCode !== undefined && template.couponCode.length >= 0) {
            formGroup.addControl('couponCode', new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('Coupon Code'), maxLength(15, 'The coupon code has a maximum of 15 characters')]))
        }

        if (template.otp !== undefined && template.otp.length >= 0) {
            formGroup.addControl("otp", new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('One-Time Password'), maxLength(15, 'The OTP has a maximum of 15 characters')]))
        }

        if (template.headerVar !== undefined && template.headerVar.length >= 0) {
            formGroup.addControl("headerVar", new FormControl({
                    value: '',
                    disabled: !editFlows
                }, [required('Header Variable'), maxLength(40)]
            ))
        }

        if (template.mediaVar !== undefined && template.mediaVar.length >= 0) {
            formGroup.addControl("mediaVar", new FormControl({
                    value: '',
                    disabled: !editFlows
                }, [required('Media')]
            ))
        }

        if (template.location !== undefined) {
            const location = new FormGroup({});
            location.addControl("locationName", new FormControl({
                    value: '',
                    disabled: !editFlows
                }, [required("Location name")]
            ));

            location.addControl("address", new FormControl({
                    value: '',
                    disabled: !editFlows
                }, [required("Address")]
            ));

            location.addControl("latitude", new FormControl({
                    value: '',
                    disabled: !editFlows
                }, [required("Latitude")]
            ));

            location.addControl("longitude", new FormControl({
                    value: '',
                    disabled: !editFlows
                }, [required("Longitude")]
            ));

            formGroup.addControl("location", location);
        }


        if (template.suggestedResponse) {

            const allowedLength = 50;
            const errorMessage = 'Additional Parameter length exceeded';

            formGroup.addControl('suggestedResponse', new FormGroup({}));
            if (Array.isArray(template.suggestedResponse)) {
                for (const responseIndex of template.suggestedResponse) {
                    (formGroup.get('suggestedResponse') as FormGroup).addControl(responseIndex['index'].toString(), new FormGroup({
                        flowStep: new FormControl('', required('flow step')),
                        additionalData: new FormControl(''),
                    }));
                }
            } else {
                // @ts-ignore
                for (const responseTypeKey in template.suggestedResponse) {
                    (formGroup.get('suggestedResponse') as FormGroup).addControl(responseTypeKey, new FormGroup({
                        flowStep: new FormControl('', required('flow step')),
                        additionalData: new FormControl(''),
                    }));
                }
            }

        }

        if (template.bodyVars) {
            const bodyVarFrom = new FormArray([]);
            for (const _ of template.bodyVars) {
                bodyVarFrom.push(this.bodyVarTemplate(editPermissions))
            }
            formGroup.addControl("bodyVars", bodyVarFrom)
        }

        if (template.websiteUrls) {
          if (Array.isArray(template.websiteUrls)) {
              const websiteUrl = new FormArray([]);
              for (const websiteUrlContent of template.websiteUrls.sort(sortByIndex)) {
                  websiteUrlContent.url =  this.urlEndpoint(websiteUrlContent.url);
                  if (websiteUrlContent.url.includes('{{1}}') || typeof websiteUrlContent.index === 'string') {
                      websiteUrl.push(this.websiteUrlsTemplate(editPermissions, websiteUrlContent));
                  }
              }
              formGroup.addControl('websiteUrls', websiteUrl);
          }
        }

        return formGroup;
    }

    static urlEndpoint(content: string) : string {
        return content.includes('http') ? content.split('/').splice(3).join('/') : content
    }

    static toTemplate(waTemplate: WaTemplate): TemplateForm {

        const templateContent = waTemplate.templateContent;
        const template: TemplateForm = {}

        template.tag = waTemplate.tag

        if (waTemplate.numOfParams > 0) {
            template.bodyVars = Array.from({length: waTemplate.numOfParams}, () => "");
        }

        if (!isEmpty(templateContent?.header) && templateContent?.header?.type !== undefined) {
            if (templateContent?.header?.type === 'text' && templateContent?.header?.text?.text?.includes("{{1}}")) {
                template.headerVar = ""
            }

            if (templateContent?.header?.type === 'media') {
                template.mediaVar = ""
            }

            if (templateContent?.header?.type === 'location') {
                template.location = {
                    address: "",
                    latitude: "",
                    longitude: "",
                    locationName: ""
                }
            }
        }

        if (!isEmpty(templateContent?.buttons) && templateContent?.buttons?.quickReply) {
            template.suggestedResponse = templateContent?.buttons?.quickReply
        }

        if (!isEmpty(templateContent?.buttons?.action?.websiteUrl) && templateContent?.buttons?.action?.websiteUrl ||
            !isEmpty(templateContent?.buttons?.action?.websiteUrls) && templateContent?.buttons?.action?.websiteUrls) {
            template.websiteUrls = templateContent.buttons.action.websiteUrls ? templateContent.buttons.action.websiteUrls
                : templateContent.buttons.action.websiteUrl
        }

        if (!isEmpty(templateContent?.buttons?.copyCode) && templateContent?.buttons?.copyCode?.text) {
            template.couponCode = ""
        }

        if (template.tag === 'Authentication') {
            template.otp = ""
        }

        if (!isEmpty(templateContent?.buttons?.otp) && templateContent?.buttons?.otp) {
            template.otp = ""
        }


        return template;
    }

    get nodeData(): {} {
        return {
            action: this.action,
            'type': 'action',
            metadata: {
                nodeVersion: '1.0.0'
            }
        };
    }

    addActionParams(params: ActionParamsForm) {

        const {
            sendTo,
            name,
            templateId,
            businessAccountId,
            integrationId,
            success,
            error,
            description,
            clientReferenceId
        } = params;

        const actionParams = {
            sendTo,
            templateId,
            businessAccountId,
            integrationId,
        };

        if (clientReferenceId.length > 0) {
            actionParams['clientReferenceId'] = clientReferenceId;
        }

        this.action = {
            ...this.action,
            description,
            name,
            actionParams,
            result: {
                success,
                error
            }
        }
    }

    addTemplateParameters(templateParams: TemplateForm) {
        this.action.actionParams.parameters = {buttons: {}}

        if (templateParams.suggestedResponse) {

            for (const responseGroup in templateParams.suggestedResponse) {
                if (!templateParams.suggestedResponse[+responseGroup]['additionalData'].length) {
                    delete templateParams.suggestedResponse[responseGroup]['additionalData'];
                }
            }
            this.action.actionParams.parameters.buttons.suggestedResponse = {}
            this.action.actionParams.parameters.buttons.suggestedResponse = templateParams.suggestedResponse
        }

        if (templateParams.couponCode) {
            this.action.actionParams.parameters.buttons.couponCode = ""
            this.action.actionParams.parameters.buttons.couponCode = templateParams.couponCode
        }

        if (templateParams.otp) {
            this.action.actionParams.parameters.buttons.otp = ""
            this.action.actionParams.parameters.buttons.otp = templateParams.otp
        }

        if (templateParams.headerVar) {
            this.action.actionParams.parameters.header = {
                text: {
                    "1": templateParams.headerVar
                }
            }
        }

        if (templateParams.mediaVar) {
            this.action.actionParams.parameters.header = {
                media: {
                    "fileId": templateParams.mediaVar
                }
            }
        }

        if (templateParams.location) {
            this.action.actionParams.parameters.header = {
                location: {
                    "name": templateParams.location.locationName,
                    "address": templateParams.location.address,
                    "latitude": templateParams.location.latitude,
                    "longitude": templateParams.location.longitude,
                }
            }
        }

        if (templateParams.bodyVars) {
            this.action.actionParams.parameters.body = {}

            for (const templateParamsKey in templateParams.bodyVars) {
                const index = +templateParamsKey + 1
                this.action.actionParams.parameters.body[index] = templateParams.bodyVars[templateParamsKey]['bodyVariable']
            }
        }

        if (templateParams.websiteUrls) {
            this.action.actionParams.parameters.buttons.websiteUrls = {};
            for(const websiteUrl of templateParams.websiteUrls) {
                this.action.actionParams.parameters.buttons.websiteUrls[websiteUrl['index']] = websiteUrl['url']
            }
        }

    }
}

const sortByIndex = (a: WaTemplateIndexUrl, b: WaTemplateIndexUrl) => {
    const nameA = a.index;
    const nameB = b.index;
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    // names must be equal
    return 0;
}


// action:
//     name: "sendTemplate1"
//     description: "send movie ticket template"
//     actionType: "sendTemplate"
//     actionParams:
//         sendTo: "2764675895"
//         templateId: "4377434585jdcfhcjcxj"
//         businessAccountId: "735f3b2853e148fc8cf499cb46ab56a9"
//         integrationId: "ff808081673696e301673fd6dbb10112"
//         parameters:
//             body:
//                 "1": "parameter 1"
//                 "2": "parameter 2"
//             header:
//                 media:
//                     fileId: "someFileId1"
//                 text:
//                     "1": [[bloo]]
//                 location:
//                     "name": "name of the location"
//                     "address": "xyz street, Capetown"
//                     "latitude": "-33.8811333"
//                     "longitude": "18.6337785"
//             buttons:
//                 websiteUrls:
//                     "1": "contact/contact-sales/"
//             suggestedResponse:
//                 "1":
//                     flowId: "0C47C00B33D1CF39DF3EF32316BF49DC"
//                     stepId: "step2"
//                     additionalData:
//                         name: "fuu"
//                 "2":
//                     flowId: "0C47C00B33D1CF39DF3EF32316BF49DC"
//                     stepId: "step8"
//                     additionalData:
//                         name: "fuu"
//     result:
//         error: "step12"
//         success: "step12"
// type: "action"
// next: "step2"
// metadata:
//     stepUniqueID: "8d740134-798a-3a16-87b7-7808db5b83df"